import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { GreenCardVisual, downloadCardPng, printCardAtAadhaarSize } from '../components/GreenCardVisual';
import { useRazorpay } from 'react-razorpay';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function GreenCard() {
  const [settings, setSettings] = useState(null);
  const [myCard, setMyCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const { Razorpay } = useRazorpay();

  const [formData, setFormData] = useState({
    full_name: '',
    dob: '',
    family_members: '',
    family_names: [],
    city: '',
    state: 'Gujarat',
    address: '',
    pincode: '',
    user_phone: localStorage.getItem('userPhone') || '',
    has_pan: false,
    has_aadhaar: false,
    has_ration: false,
    has_election: false,
    has_property: false,
    has_agriculture: false
  });

  // Watch for phone updates in localStorage
  useEffect(() => {
    const phone = localStorage.getItem('userPhone');
    if (phone && formData.user_phone !== phone) {
      setFormData(prev => ({ ...prev, user_phone: phone }));
    }
  }, [showForm]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, profileRes] = await Promise.all([
        axios.get(`${API_URL}/api/greencard/settings`),
        axios.get(`${API_URL}/api/greencard/my-card`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('userToken')}` }
        }).catch(() => ({ data: null }))
      ]);
      setSettings(settingsRes.data);
      if (profileRes.data) {
        setMyCard(profileRes.data);
      } else {
        const phone = localStorage.getItem('userPhone');
        if (phone && !formData.user_phone) {
           // prepopulate user phone
           setFormData(prev => ({ ...prev, user_phone: phone }));
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyClick = () => {
    if (!localStorage.getItem('userToken')) {
      toast.error('કૃપા કરીને અરજી કરવા માટે લોગિન કરો (Please login to apply)');
      return;
    }
    setShowForm(true);
  };

  const handleRenew = async () => {
    await processPayment();
  };

    const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name || !formData.dob || !formData.family_members || !formData.city || !formData.address.trim()) {
      toast.error('બધી વિગતો ભરો (Fill all fields)');
      return;
    }
    // Verify all dynamic family names are filled
    const count = parseInt(formData.family_members) || 0;
    if (count > 0) {
      const emptyNames = formData.family_names.slice(0, count).filter(n => !n || !n.trim());
      if (emptyNames.length > 0) {
         toast.error('કૃપા કરીને બધા કુટુંબના સભ્યોના નામ ભરો (Fill all family names)');
         return;
      }
    }
    await processPayment(formData);
  };

  const processPayment = async (appData = null) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      
      const payload = appData || myCard; // Use existing data for renewal
      
      const orderRes = await axios.post(`${API_URL}/api/greencard/create-order`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const options = {
        key: orderRes.data.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderRes.data.amount,
        currency: "INR",
        order_id: orderRes.data.id,
        name: "કાગજબુક ઓનલાઈન",
        description: "ગ્રીન કાર્ડ મેમ્બરશિપ (Green Card Membership)",
        handler: async (res) => {
          setLoading(true);
          try {
            await axios.post(`${API_URL}/api/greencard/verify-payment`, {
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_signature: res.razorpay_signature
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('ચુકવણી સફળ રહી (Payment successful)');
            
            // Google Ads Conversion Tracking (Green Card Payment)
            if (window.gtag) {
              window.gtag('event', 'purchase', {
                'event_category': 'greencard_payment_complete',
                'event_label': 'Green Card Membership',
                'value': orderRes.data.amount / 100,
                'currency': 'INR',
                'transaction_id': res.razorpay_payment_id
              });
            }

            await fetchData();
            setShowForm(false);
          } catch(err) {
             toast.error('ચુકવણીની પુષ્ટિ કરવામાં ભૂલ.');
          } finally {
             setLoading(false);
          }
        },
        theme: { color: "#16a34a" }
      };

      const rzp = new Razorpay(options);
      rzp.on('payment.failed', function (response){
         toast.error("ચુકવણી નિષ્ફળ: " + response.error.description);
      });
      rzp.open();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 401 || status === 403) {
        toast.error('સત્ર સમાપ્ત થયું, કૃપા કરીને ફરીથી લોગિન કરો (Please login again)');
      } else {
        const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || err.message);
        toast.error(`ઓર્ડર બનાવવામાં નિષ્ફળ: ${msg}`);
      }
      console.error('Green Card order error:', status, detail);
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = () => {
    if (cardRef.current === null) return;
    downloadCardPng(cardRef.current, myCard.card_number)
      .then(() => toast.success('કાર્ડ ડાઉનલોડ થઈ ગયું (Card downloaded)'))
      .catch((err) => { console.error(err); toast.error('કાર્ડ ડાઉનલોડ કરવામાં ભૂલ'); });
  };

  const handleCreateOrderBackendOnly = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const orderRes = await axios.post(`${API_URL}/api/greencard/create-order`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return orderRes;
    } catch(err) {
      throw err;
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center font-bold text-xl text-green-700">Loading...</div>;
  }

  const isExpired = myCard && new Date(myCard.valid_until) < new Date();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      <Header />
      
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-3">
        {!myCard && !showForm && (
          <div data-testid="greencard-landing" className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-[#f8fafc] px-4 py-3 flex flex-col items-center justify-center border-b border-gray-200">
              <h1 className="text-2xl font-extrabold text-[#172554] text-gujarati mb-3 text-center drop-shadow-sm" data-testid="greencard-title">કાગજબુક ગ્રીન કાર્ડ (Membership)</h1>
              
              <div className="w-full max-w-[300px] aspect-[85.6/54] rounded-2xl overflow-hidden shadow-xl relative bg-white border border-gray-200 flex flex-col transform hover:scale-[1.02] transition-transform duration-300">
                <div className="bg-[#172554] p-2 text-center relative border-b-4 border-green-500">
                  <div className="flex items-center justify-center gap-2 mb-1"><div className="w-7 h-7 bg-white rounded-full p-0.5 overflow-hidden"><img src="/kagajbook-logo.png" alt="કાગજબુક" className="w-full h-full object-contain" /></div><h2 className="text-white text-lg font-bold text-gujarati leading-none">કાગજબુક</h2></div>
                  <div className="bg-green-500 text-[#172554] text-[10px] font-bold inline-block px-2.5 py-0.5 rounded-full text-gujarati">ગ્રીન કાર્ડ</div>
                </div>
                
                <div className="flex-1 p-3 relative bg-gradient-to-br from-green-50 to-green-100/50">
                  <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#16a34a 2px, transparent 2px)', backgroundSize: '16px 16px' }}></div>
                  <div className="space-y-3 relative z-10">
                    <div className="w-32 h-2.5 bg-gray-200/80 rounded-full animate-pulse"></div>
                    <div className="w-24 h-2.5 bg-gray-300/80 rounded-full animate-pulse"></div>
                    <div className="pt-2 grid grid-cols-2 gap-3">
                      <div className="w-16 h-2 bg-gray-200/80 rounded-full animate-pulse"></div>
                      <div className="w-20 h-2 bg-gray-200/80 rounded-full animate-pulse"></div>
                    </div>
                  </div>
                  <div className="absolute right-4 top-4 opacity-20">
                    <svg className="w-12 h-12 text-[#172554]" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
                  </div>
                </div>
                
                <div className="bg-green-600 h-2 w-full"></div>
              </div>
            </div>
            
            <div className="px-5 py-3">
              <h3 className="text-lg font-bold text-[#172554] mb-2 border-b pb-1 text-gujarati">ગ્રીન કાર્ડના ફાયદાઓ (Benefits)</h3>
              <ul className="space-y-1.5 mb-3" data-testid="greencard-benefits-list">
                {settings?.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`greencard-benefit-${i}`}>
                    <span className="text-green-600 bg-green-100 rounded-full p-1 mt-0.5 shrink-0">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                    </span>
                    <span className="font-bold text-gray-700 text-gujarati leading-relaxed text-sm">{b}</span>
                  </li>
                ))}
              </ul>
              
              <button 
                onClick={handleApplyClick}
                data-testid="greencard-apply-btn"
                className="w-full bg-green-600 hover:bg-green-700 text-white text-lg font-bold py-2.5 rounded-lg shadow-md transition-colors text-gujarati"
              >
                અરજી કરો (Apply Now)
              </button>
            </div>
          </div>
        )}

        {showForm && !myCard && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            <h2 className="text-2xl font-bold text-[#172554] mb-6 text-gujarati border-b pb-4">ગ્રીન કાર્ડ અરજી ફોર્મ</h2>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="greencard-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">પૂરું નામ (Full Name) *</label>
                  <input type="text" required className="w-full border border-gray-300 rounded-lg p-3 outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 text-gujarati" value={formData.full_name} onChange={e=>setFormData({...formData, full_name: e.target.value})} placeholder="As per Aadhaar/PAN" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">મોબાઈલ નંબર (Mobile Number) *</label>
                  <input type="text" readOnly className="w-full border border-gray-300 rounded-lg p-3 bg-gray-50 text-gray-500 font-bold" value={formData.user_phone} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">જન્મ તારીખ (Date of Birth) *</label>
                  <input type="date" required className="w-full border border-gray-300 rounded-lg p-3 outline-none focus:border-green-500" value={formData.dob} onChange={e=>setFormData({...formData, dob: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">કુટુંબના સભ્યોની સંખ્યા (Number of Family Members) *</label>
                  <input type="number" min="1" max="20" required className="w-full border border-gray-300 rounded-lg p-3 outline-none focus:border-green-500 text-gujarati" value={formData.family_members} onChange={e=>{
                    const count = parseInt(e.target.value) || 0;
                    const newNames = [...formData.family_names];
                    newNames.length = count;
                    for(let i=0; i<count; i++) if(newNames[i]===undefined) newNames[i]='';
                    setFormData({...formData, family_members: e.target.value, family_names: newNames});
                  }} placeholder="ઉદાહરણ તરીકે: 4" />
                </div>
              </div>
              
              {formData.family_names.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-2">
                  <label className="block text-sm font-bold text-[#172554] mb-3 text-gujarati border-b pb-2">કુટુંબના સભ્યોના નામ (Family Member Names)</label>
                  <div className="space-y-3">
                    {formData.family_names.map((name, idx) => (
                      <div key={idx}>
                        <input 
                          type="text" required 
                          className="w-full border border-gray-300 rounded-lg p-2.5 outline-none focus:border-green-500 text-gujarati"
                          placeholder={`સભ્ય ${idx + 1} નું નામ`}
                          value={name || ''}
                          onChange={e => {
                            const updatedNames = [...formData.family_names];
                            updatedNames[idx] = e.target.value;
                            setFormData({...formData, family_names: updatedNames});
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">શહેર (City) *</label>
                  <select required className="w-full border border-gray-300 rounded-lg p-3 outline-none focus:border-green-500 text-gujarati" value={formData.city} onChange={e=>setFormData({...formData, city: e.target.value})}>
                    <option value="">-- પસંદ કરો --</option>
                    {settings?.allowed_cities.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">રાજ્ય (State) *</label>
                  <input type="text" readOnly className="w-full border border-gray-300 rounded-lg p-3 bg-gray-50 text-gray-500 text-gujarati" value={formData.state} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 text-gujarati">સંપર્ક સરનામું (Communication Address) *</label>
                <textarea rows={3} required data-testid="greencard-address-input" className="w-full border border-gray-300 rounded-lg p-3 outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 text-gujarati resize-none" placeholder="તમારું સંપૂર્ણ સરનામું દાખલ કરો..." value={formData.address} onChange={e=>setFormData({...formData, address: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 text-gujarati">પિન કોડ (PIN Code) *</label>
                <input type="text" maxLength={6} required data-testid="greencard-pincode-input" className="w-full border border-gray-300 rounded-lg p-3 outline-none focus:border-green-500 text-gujarati" placeholder="395010" value={formData.pincode} onChange={e=>setFormData({...formData, pincode: e.target.value.replace(/\D/g, '')})} />
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200 mt-2">
                <label className="block text-lg font-bold text-[#172554] mb-4 text-gujarati border-b border-green-300 pb-2">તમારી પાસે કયા દસ્તાવેજો છે? (Which documents do you have?)</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: 'has_pan', label: 'પાન કાર્ડ (PAN Card)' },
                    { key: 'has_aadhaar', label: 'આધાર કાર્ડ (Aadhaar Card)' },
                    { key: 'has_ration', label: 'રેશન કાર્ડ (Ration Card)' },
                    { key: 'has_election', label: 'ચૂંટણી કાર્ડ (Election Card)' },
                    { key: 'has_property', label: 'રહેણાંક મિલકત ધરાવો છો? (Do you own residential property?)' },
                    { key: 'has_agriculture', label: 'ખેતીની જમીન ધરાવો છો? (Do you own agricultural land?)' },
                  ].map(doc => (
                    <div key={doc.key} className="flex items-center justify-between bg-white p-3 rounded shadow-sm border border-gray-100">
                      <span className="text-sm font-bold text-gray-700 text-gujarati">{doc.label}</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" checked={formData[doc.key]} onChange={e => setFormData({...formData, [doc.key]: e.target.checked})} />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-4 flex gap-4">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-3 rounded-lg transition-colors">રદ કરો (Cancel)</button>
                <button type="submit" className="flex-[2] bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg shadow-md transition-colors text-gujarati">ચુકવણી કરો (Pay ₹{settings?.fee_amount})</button>
              </div>
            </form>
          </div>
        )}

        {myCard && (
          <div className="flex flex-col items-center">
            {isExpired && (
              <div className="w-full max-w-md bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-6 text-center shadow-sm">
                <p className="font-bold text-lg text-gujarati mb-3">તમારું ગ્રીન કાર્ડ એક્સપાયર થઈ ગયું છે.</p>
                <button onClick={handleRenew} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 px-6 rounded-lg shadow transition-colors text-gujarati">
                  નવીકરણ કરો (Renew ₹{settings?.fee_amount})
                </button>
              </div>
            )}

            <GreenCardVisual ref={cardRef} card={myCard} />

            <div className="mt-8 flex gap-4 w-full max-w-md">
              <button onClick={downloadImage} className="flex-1 bg-[#172554] hover:bg-blue-900 text-white font-bold py-3 rounded-lg shadow-md transition-colors flex items-center justify-center gap-2 text-gujarati">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                ડાઉનલોડ કરો (Download)
              </button>
              <button onClick={() => printCardAtAadhaarSize(cardRef.current, myCard.card_number).catch(() => toast.error('પ્રિન્ટ કરવામાં ભૂલ'))} data-testid="greencard-print-btn" className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg shadow-md transition-colors flex items-center justify-center gap-2 text-gujarati">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path></svg>
                પ્રિન્ટ કરો (Print Card)
              </button>
            </div>
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
}
